import { z } from 'zod';
import { ANKAUF_MERKMALE, type AnkaufMerkmal } from './ankaufKatalog';
import { VORLAGE_NAMESPACE } from './branchen';
import { PORTAL_PRODUKT_GRENZEN, PortalProduktSchema, type PortalProduktFormular } from './portalProdukte';
import { gtinPruefziffer } from './produkte';

/**
 * Marktplatz-Konto → Produkte der Händler bearbeiten (mp_product_edit der PHP-Middleware) und KI je Feld (mp_ai_text).
 * Der Marktplatz ändert Titel, Beschreibung, Marke, Kategorie, Bilder, Produktfelder und je Variante Preis und EAN.
 * SKU, Bestand und Varianten bleiben Sache des Händlers. Eigene Änderungen bleiben bei künftigen Abgleichen erhalten.
 */

/** Was der Editor braucht – für Portal und Konto gleich */
export type ProduktFormular = PortalProduktFormular;

/** Felder, die der Marktplatz als „eigene Fassung“ geändert hat (produkt.mp_felder) */
export const MP_STAMMFELDER = ['titel', 'beschreibung', 'marke', 'bilder', 'preis', 'ean'] as const;
export type MpStammfeld = (typeof MP_STAMMFELDER)[number];
export const MP_FELD_TITEL: Record<MpStammfeld, string> = {
  titel: 'Titel', beschreibung: 'Beschreibung', marke: 'Marke', bilder: 'Bilder', preis: 'Preis', ean: 'EAN',
};
/** Produktfelder als „felder.namespace.key“ */
export const mpFeldProduktfeld = (voll: string) => `felder.${voll}`;
/** Anzeigename eines Eintrags aus mp_felder (Stammfeld oder Produktfeld) */
export const mpFeldName = (f: string, felder: readonly { voll: string; name: string }[]): string =>
  f.startsWith('felder.') ? (felder.find((x) => x.voll === f.slice(7))?.name ?? f.slice(7)) : (MP_FELD_TITEL[f as MpStammfeld] ?? f);

const zahlEingabe = z.preprocess((v) => {
  if (v === '' || v === null || v === undefined) return null;
  if (typeof v === 'string') return Number(v.replace(/\s+/g, '').replace(',', '.'));
  return v;
}, z.number({ invalid_type_error: 'Bitte eine Zahl angeben' }).finite('Bitte eine Zahl angeben').nullable());

/** Kopfdaten wie im Portal (gleiche Grenzen und Meldungen) */
const Kopf = PortalProduktSchema.innerType().pick({ titel: true, beschreibung: true, marke: true, kategorie: true, bilder: true, felder: true });

export const KontoVarianteSchema = z.object({
  id: z.string().uuid(),
  ean: z.string().max(40).default('').transform((v) => v.replace(/\D/g, '')),
  preis: zahlEingabe,
  preisAlt: zahlEingabe.default(null),
});
export type KontoVarianteEingabe = z.input<typeof KontoVarianteSchema>;

export const KontoProduktSchema = Kopf.extend({
  varianten: z.array(KontoVarianteSchema).max(PORTAL_PRODUKT_GRENZEN.varianten).default([]),
}).superRefine((d, ctx) => {
  const fehler = (path: (string | number)[], message: string) => ctx.addIssue({ code: 'custom', path, message });
  d.varianten.forEach((v, i) => {
    const nr = i + 1;
    if (v.ean && (![8, 12, 13, 14].includes(v.ean.length) || !gtinPruefziffer(v.ean))) fehler(['varianten', i, 'ean'], `Variante ${nr}: Die EAN „${v.ean}“ ist ungültig (Prüfziffer).`);
    if (v.preis === null || v.preis <= 0) fehler(['varianten', i, 'preis'], `Variante ${nr}: Bitte einen Preis angeben.`);
    else if (v.preis > 9_999_999) fehler(['varianten', i, 'preis'], 'Der Preis ist zu hoch.');
    if (v.preisAlt !== null && (v.preisAlt < 0 || v.preisAlt > 9_999_999)) fehler(['varianten', i, 'preisAlt'], 'Ungültiger Preis.');
  });
});
export type KontoProduktEingabe = z.input<typeof KontoProduktSchema>;
export type KontoProduktDaten = z.output<typeof KontoProduktSchema>;

/** Kategorie direkt in der Liste setzen (mp_set_cat) – leer entfernt sie */
export const ProduktKategorieSchema = z.object({ kategorie: z.string().trim().max(120) });

/** Eigene Fassung zurücknehmen – beim nächsten Abgleich gilt wieder der Wert des Händlers (mp_product_reset) */
export const FassungZuruecksetzenSchema = z.object({
  /** leer = alle */
  felder: z.array(z.string().max(140)).max(300).default([]),
});

/* ------------------------------------------------------------ KI je Feld */

/** Modi für Titel und Beschreibung (mp_ai_text: titel | text | beides) */
export const KI_TEXT_MODI = {
  verbessern: 'Verbessern',
  neu: 'Neu schreiben',
  seo: 'Für Suchmaschinen',
} as const;
export type KiTextModus = keyof typeof KI_TEXT_MODI;

/** Stammdaten, die die KI vorschlagen kann (anr_luecken) */
export const KI_STAMMFELDER = ['titel', 'beschreibung', 'marke', 'kategorie'] as const;
export type KiStammfeld = (typeof KI_STAMMFELDER)[number];
export const KI_STAMMFELD_TITEL: Record<KiStammfeld, string> = { titel: 'Titel', beschreibung: 'Beschreibung', marke: 'Marke', kategorie: 'Kategorie' };

/** Ungespeicherte Werte aus dem Editor – die KI arbeitet mit dem, was gerade im Formular steht */
export const ProduktEntwurfSchema = z.object({
  titel: z.string().max(500).optional(),
  beschreibung: z.string().max(100_000).optional(),
  marke: z.string().max(160).optional(),
  kategorie: z.string().max(120).optional(),
  felder: z.record(z.string().max(130), z.string().max(2000)).optional(),
});
export type ProduktEntwurf = z.input<typeof ProduktEntwurfSchema>;

export const KiFeldSchema = z.object({
  /** titel | beschreibung | marke | kategorie | namespace.key */
  feld: z.string().min(1).max(130),
  modus: z.enum(['verbessern', 'neu', 'seo']).default('verbessern'),
  entwurf: ProduktEntwurfSchema.default({}),
});
export type KiFeldEingabe = z.input<typeof KiFeldSchema>;

export interface KiFeldVorschlag {
  feld: string;
  name: string;
  wert: string;
  alt: string;
  begruendung: string;
  sicherheit: number;
}

export interface KiFeldErgebnis {
  /** null = die KI konnte nichts Sicheres ableiten */
  vorschlag: KiFeldVorschlag | null;
  /** Kategorie: keine vorhandene passt – Vorschlag für eine neue */
  neueKategorie: string | null;
  kosten: { tokens: number; cent: number } | null;
}

/* ------------------------------------------------------------ Kategorien: KI-Vorschau in der Liste */

export interface KategorieKiZuordnung {
  id: string;
  titel: string;
  /** bisherige Kategorie */
  alt: string;
  /** vorhandene Kategorie, leer = keine passt */
  kategorie: string;
  /** Vorschlag für eine neue Kategorie */
  neu: string;
  /** passt nicht zum Sortiment */
  unpassend: boolean;
  hinweis: string;
}

export interface KategorieKiVorschau {
  geprueft: number;
  zuordnungen: KategorieKiZuordnung[];
  weitere: boolean;
  kosten: { tokens: number; cent: number } | null;
}

/* ------------------------------------------------------------ Katalog-Vorschläge im Editor (Uhren-Katalog des Ankaufs) */

export const KATALOG_VORSCHLAG_FELDER = ['marke', 'modell', 'referenz'] as const;
export type KatalogVorschlagFeld = (typeof KATALOG_VORSCHLAG_FELDER)[number];

export const KatalogVorschlagAbfrage = z.object({
  feld: z.enum(KATALOG_VORSCHLAG_FELDER),
  marke: z.string().trim().max(80).default(''),
  modell: z.string().trim().max(120).default(''),
  q: z.string().trim().max(120).default(''),
});
export type KatalogVorschlagAbfrage = z.input<typeof KatalogVorschlagAbfrage>;

export interface KatalogVorschlag {
  wert: string;
  /** Zusatz, z. B. referenz_detail */
  zusatz: string;
  /** nur bei Referenzen: Werte für leere Produktfelder (namespace.key → Wert, schon passend zum Feld) */
  felder?: Record<string, string>;
}

export interface KatalogVorschlagSeite {
  /** der Marktplatz hat aktive Katalogeinträge – sonst bleibt das Feld ein normales Textfeld */
  aktiv: boolean;
  eintraege: KatalogVorschlag[];
}

/** Vorlagenfelder für Modell und Referenz (Branchen-Vorlage Uhren) */
export const KATALOG_FELD_MODELL = `${VORLAGE_NAMESPACE}.modell`;
export const KATALOG_FELD_REFERENZ = `${VORLAGE_NAMESPACE}.referenz`;

/** Merkmale des Uhren-Katalogs → Felder der Branchen-Vorlage (namespace.key) */
export const KATALOG_MERKMAL_FELDER: Record<AnkaufMerkmal | 'merkmale', string> = {
  groesse: `${VORLAGE_NAMESPACE}.gehaeusedurchmesser`,
  glas: `${VORLAGE_NAMESPACE}.uhrglas`,
  zifferblatt: `${VORLAGE_NAMESPACE}.zifferblattfarbe`,
  gehaeuse: `${VORLAGE_NAMESPACE}.gehaeusematerial`,
  armband: `${VORLAGE_NAMESPACE}.armbandmaterial`,
  werk: `${VORLAGE_NAMESPACE}.werk`,
  geschlecht: `${VORLAGE_NAMESPACE}.geschlecht`,
  merkmale: `${VORLAGE_NAMESPACE}.funktionen`,
};
/** Bezeichnungen wie im Katalog (für Hinweise) */
export const KATALOG_MERKMAL_TITEL: Record<AnkaufMerkmal | 'merkmale', string> = { ...ANKAUF_MERKMALE, merkmale: 'Merkmale' };
